import React, { useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { House, UserRound } from "lucide-react";
import { initialsOf, toneOf } from "@/components/Inbox/inboxModel";
import { pageURLs } from "@/constants";
import WantListingThumb from "@/components/WantListingThumb";
import { wantListingLinkProps } from "@/helpers/wantListingLink";
import { directMessageUrl } from "@/helpers/directMessageUrl";
import { addContactToClients } from "@/services/resultsService";
import { buildLeads, kindOf, PREVIEW, splitOnTitle } from "./leadsModel";
import "./ResultsLeads.scss";

const introChatPath = (introId) => `${pageURLs.messages}?Id=${btoa(`intro-${introId}`)}`;

const networkAction = (item, navigate) => {
  if ((item.kind === "intro_received" || item.kind === "intro_replied") && item.introductionId) {
    return { label: "Open", onClick: () => navigate(introChatPath(item.introductionId)) };
  }
  if (item.kind === "message" && item.agent?.userId) {
    return { label: "Reply", onClick: () => navigate(directMessageUrl(item.agent.userId)) };
  }
  if (item.kind === "match" && item.listingKey) {
    return {
      label: "View",
      onClick: () => navigate(`/mls-listing/${encodeURIComponent(item.listingKey)}?from=match`),
    };
  }
  return null;
};

const gitchaAction = (item, navigate) => {
  if (item.contactType === "buyer_form" && item.draftWantAdId) {
    return { label: "Review", onClick: () => navigate(`/edit-want-listing-agent/${item.draftWantAdId}`) };
  }
  if (item.contactType === "message") {
    return { label: "Open", onClick: () => navigate(pageURLs.messages) };
  }
  return null;
};

const LinkedLine = ({ text, listing, navigate }) => {
  const parts = splitOnTitle(text, listing?.title);
  if (parts.length === 1) return text;
  return (
    <>
      {parts[0]}
      <a className="results-lead__listing" {...wantListingLinkProps(navigate, listing)}>
        {parts[1]}
      </a>
      {parts[2]}
    </>
  );
};

const LeadRow = ({ lead, action, secondary, navigate }) => (
  <li className={`results-lead${lead.isNew ? " is-new" : ""}`}>
    {lead.isPlace ? (
      <span className="results-lead__place" aria-hidden="true">
        <House size={18} strokeWidth={1.8} />
      </span>
    ) : lead.anonymous ? (
      <span className="results-lead__place" aria-hidden="true">
        <UserRound size={18} strokeWidth={1.8} />
      </span>
    ) : (
      <span className={`results-lead__avatar is-tone-${toneOf(lead.name)}`} aria-hidden="true">
        {initialsOf(lead.name)}
      </span>
    )}
    <div className="results-lead__text">
      <p className="results-lead__who">
        {lead.isNew ? <span className="results-lead__dot" aria-label="New" role="img" /> : null}
        <span className="results-lead__name">{lead.name}</span>
        {lead.company ? <span className="results-lead__company">{lead.company}</span> : null}
      </p>
      <p className="results-lead__what" title={[lead.what, lead.about].filter(Boolean).join(" · ")}>
        <LinkedLine text={lead.what} listing={lead.listing} navigate={navigate} />
        {lead.about ? (
          <span className="results-lead__about">
            {" · "}
            <LinkedLine text={lead.about} listing={lead.listing} navigate={navigate} />
          </span>
        ) : null}
        {lead.repeat ? <span className="results-lead__repeat"> · {lead.repeat}</span> : null}
      </p>
    </div>
    <span className="results-lead__thumbcell">
      {lead.listing && !lead.isPlace ? (
        <a className="results-lead__thumb" tabIndex={-1} aria-hidden="true" {...wantListingLinkProps(navigate, lead.listing)}>
          <WantListingThumb map={lead.listing.map} width={56} height={40} title={lead.listing.title || "Buyer Want-Listing"} />
        </a>
      ) : null}
    </span>
    <div className="results-lead__meta">
      <span className="results-lead__when">{lead.item.at ? moment.utc(lead.item.at).local().fromNow().replace(/ /g, "\u00a0") : ""}</span>
      {action || secondary ? (
        <span className="results-lead__actions">
          {secondary ? (
            <button type="button" className="results-lead__link is-quiet" onClick={secondary.onClick} disabled={secondary.disabled}>
              {secondary.label}
            </button>
          ) : null}
          {action ? (
            <button type="button" className="results-lead__link" onClick={action.onClick}>
              {action.label}
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  </li>
);

const sourcesOf = (leads) => {
  const names = [...new Set(leads.map((lead) => ((lead.about || "").match(/Came from ([^·]+)/) || [])[1]?.trim()).filter(Boolean))];
  if (!names.length) return null;
  return names.length === 1 ? `from ${names[0]}` : `from ${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const AnonymousRow = ({ leads }) => {
  const taps = leads.length;
  const latest = leads.map((lead) => lead.item.at).filter(Boolean).sort().pop();
  const actions = [...new Set(leads.map((lead) => (lead.what || "").replace(/ on your Gitcha bio$/, "").replace(/ from your Gitcha bio$/, "")))]
    .map((text, index) => (index ? text.charAt(0).toLowerCase() + text.slice(1) : text))
    .join(", ");
  return (
    <li className="results-lead is-anonymous">
      <span className="results-lead__place" aria-hidden="true">
        <UserRound size={18} strokeWidth={1.8} />
      </span>
      <div className="results-lead__text">
        <p className="results-lead__who">
          <span className="results-lead__name">
            {taps === 1 ? "1 visitor" : `${taps} visitors`} who didn't leave a&nbsp;name
          </span>
        </p>
        <p className="results-lead__what">{[actions, sourcesOf(leads)].filter(Boolean).join(" · ")}</p>
      </div>
      <span className="results-lead__thumbcell" />
      <div className="results-lead__meta">
        <span className="results-lead__when">{latest ? moment.utc(latest).local().fromNow().replace(/ /g, "\u00a0") : ""}</span>
      </div>
    </li>
  );
};

const ResultsLeads = ({ items = [], kind = "network", networkName = "LAR" }) => {
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const [added, setAdded] = useState({});
  const [busy, setBusy] = useState({});
  const addClient = (id) => {
    if (busy[id]) return;
    setBusy((current) => ({ ...current, [id]: true }));
    addContactToClients(id)
      .then(() => setAdded((current) => ({ ...current, [id]: true })))
      .catch(() => setAdded((current) => ({ ...current, [id]: "failed" })))
      .finally(() => setBusy((current) => ({ ...current, [id]: false })));
  };

  const clientAction = (item) => {
    if (kindOf(item, kind) !== "gitcha") return null;
    if (added[item.id] === "failed") {
      return {
        label: busy[item.id] ? "Adding..." : "Couldn't add. Try again",
        onClick: () => addClient(item.id),
        disabled: Boolean(busy[item.id]),
      };
    }
    if (item.clientId || added[item.id]) {
      return { label: "In My Clients", onClick: () => navigate(pageURLs.agentMyClients) };
    }
    if (!item.canAddToClients) return null;
    return {
      label: busy[item.id] ? "Adding..." : "Add to My Clients",
      onClick: () => addClient(item.id),
      disabled: Boolean(busy[item.id]),
    };
  };

  const actionOf = (item) =>
    kindOf(item, kind) === "gitcha" ? gitchaAction(item, navigate) : networkAction(item, navigate);
  const everyone = buildLeads(items, kind, undefined, networkName);
  const leads = everyone.filter((lead) => !lead.anonymous);
  const anonymous = everyone.filter((lead) => lead.anonymous);
  const fresh = leads.filter((lead) => lead.isNew).length;
  const shown = all ? leads : leads.slice(0, PREVIEW);
  const title = kind === "gitcha" ? "Leads from Gitcha.com" : "People who reached out";
  const emptyGitcha = kind === "gitcha";

  return (
    <section className="results-card results-leads" aria-labelledby={`results-leads-${kind}`}>
      <div className="results-leads__head">
        <h3 id={`results-leads-${kind}`} className="results-leads__title">
          {title}
        </h3>
        {fresh ? <span className="results-leads__fresh">{fresh} new</span> : null}
      </div>
      {everyone.length ? (
        <>
          <ul className="results-leads__list">
            {shown.map((lead) => (
              <LeadRow
                key={lead.key}
                lead={lead}
                action={actionOf(lead.item)}
                secondary={clientAction(lead.item)}
                navigate={navigate}
              />
            ))}
            {anonymous.length ? <AnonymousRow leads={anonymous} /> : null}
          </ul>
          {leads.length > PREVIEW ? (
            <button type="button" className="results-leads__more" onClick={() => setAll((value) => !value)}>
              {all ? "See less" : "See more"}
            </button>
          ) : null}
        </>
      ) : (
        <div className="results-leads__empty">
          <p>
            {emptyGitcha
              ? "No one has contacted you from Gitcha.com in this period. Sharing your Gitcha bio link gives people a way to reach you."
              : kind === "all"
                ? `No one reached out in this period. Sharing a Buyer Want-Listing puts your buyer in front of ${networkName} agents with a match.`
                : `No ${networkName} agents have reached out in this period. Sharing a Buyer Want-Listing puts your buyer in front of agents with a match.`}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              navigate(emptyGitcha ? `${pageURLs.settings}?tab=profile` : pageURLs.agentMyWantAdListings)
            }
          >
            {emptyGitcha ? "Share your Gitcha bio" : "Share a Buyer Want-Listing"}
          </button>
        </div>
      )}
    </section>
  );
};

export default ResultsLeads;
